<template>
  <!--
    매장 요청 문구 편집(V5 — `PUT /telInfo/phrases`). 주방 문구는 POS 주방 요청 칩과 고객 취향(결정 21), 라이더 문구는 주소의
    라이더 요청 칩이다. 종류마다 목록을 통째로 바꾸고, **두 종류를 늘 함께** 보낸다(출처가 매장에 하나라 한 종류만 매장 행이
    되는 상태를 만들지 않는다). 읽기는 화면(`TelInfoView`)이 한 번 하고 이 창은 받은 값을 고친다.
  -->
  <el-dialog
    class="phrase-dialog"
    :title="$t(T.phraseDialogTitle)"
    :visible="visible"
    width="640px"
    append-to-body
    @open="resetDraft"
    @update:visible="$emit('update:visible', $event)">
    <el-alert
      v-if="draft.source === SOURCE.DEFAULT"
      class="phrase-dialog__default"
      type="info"
      :closable="false"
      :title="$t(T.defaultNotice)"
      show-icon />

    <section v-for="kind in kinds" :key="kind.key" class="phrase-dialog__kind" :data-kind="kind.key">
      <h4 class="phrase-dialog__title">{{ $t(kind.title) }}</h4>
      <p class="phrase-dialog__help">{{ $t(kind.help) }}</p>
      <ol class="phrase-dialog__list">
        <li v-for="(text, index) in draft[kind.key]" :key="text" class="phrase-dialog__item">
          <span class="phrase-dialog__text">{{ text }}</span>
          <template v-if="canSave">
            <el-button
              size="mini"
              icon="el-icon-top"
              :disabled="index === 0"
              @click="move(kind.key, index, -1)"></el-button>
            <el-button
              size="mini"
              icon="el-icon-bottom"
              :disabled="index === draft[kind.key].length - 1"
              @click="move(kind.key, index, 1)"></el-button>
            <el-button size="mini" @click="remove(kind.key, index)">{{ $t(T.removePhrase) }}</el-button>
          </template>
        </li>
      </ol>
      <div v-if="canSave" class="phrase-dialog__add">
        <el-input
          v-model="inputs[kind.key]"
          size="small"
          :maxlength="limits.phrase"
          :placeholder="$t(T.phrasePlaceholder)"
          @keyup.enter.native="add(kind.key)"></el-input>
        <el-button size="small" icon="el-icon-plus" @click="add(kind.key)">{{ $t(T.addPhrase) }}</el-button>
      </div>
      <p v-if="errors[kind.key]" class="phrase-dialog__error">{{ errors[kind.key] }}</p>
    </section>

    <p class="phrase-dialog__help">{{ $t(T.keptNotice) }}</p>
    <p v-if="saveError" class="phrase-dialog__error">{{ saveError }}</p>

    <span slot="footer">
      <el-button
        v-if="canSave && draft.source === SOURCE.STORE"
        size="small"
        :disabled="saving"
        @click="resetDefault"
        >{{ $t(T.resetDefault) }}</el-button
      >
      <el-button size="small" @click="$emit('update:visible', false)">{{ $t('닫기') }}</el-button>
      <el-button v-if="canSave" size="small" type="primary" :loading="saving" @click="save">{{ $t('저장') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { httpErrorBody, httpErrorMessage, rejectionFieldsOf } from '@/plugins/http/policy'
import {
  PHRASES_RESET_BODY,
  PHRASE_KINDS,
  PHRASE_SOURCE,
  TEL_INFO_LIMITS,
  TEL_INFO_PHRASES_API,
  TEL_INFO_TEXT,
  addPhrase,
  movePhrase,
  phraseDraftOf,
  phrasesBodyOf,
  removePhrase,
} from '@/utils/telInfo'

/** 저장 요청 — 실패는 이 창 안에서 말한다(토스트와 두 번 말하지 않게) */
const SAVE_CONFIG = Object.freeze({ showErrorMessage: false })

/** 종류별 빈 칸 — 입력·오류 */
const blankByKind = () => PHRASE_KINDS.reduce((acc, kind) => Object.assign(acc, { [kind.key]: '' }), {})

export default {
  name: 'RequestPhraseDialog',

  props: {
    visible: { type: Boolean, default: false },
    /** 받은 요청 문구(V5 응답 `{storeCd, kitchen, rider, source}`) */
    phrases: { type: Object, default: null },
    /** `TelInfoPhraseUp` 이 있는가 — 없으면 보기만 한다 */
    canSave: { type: Boolean, default: false },
  },

  data() {
    return {
      T: TEL_INFO_TEXT,
      SOURCE: PHRASE_SOURCE,
      kinds: PHRASE_KINDS,
      limits: TEL_INFO_LIMITS,
      draft: phraseDraftOf(this.phrases),
      inputs: blankByKind(),
      errors: blankByKind(),
      saveError: '',
      saving: false,
    }
  },

  methods: {
    /** 창을 열 때마다 받은 값에서 다시 시작한다 — 닫은 창의 고침은 저장한 것이 아니다 */
    resetDraft() {
      this.draft = phraseDraftOf(this.phrases)
      this.inputs = blankByKind()
      this.errors = blankByKind()
      this.saveError = ''
    },

    add(kind) {
      const next = addPhrase(this.draft[kind], this.inputs[kind])
      this.errors = Object.assign({}, this.errors, { [kind]: next.error ? this.$t(next.error) : '' })
      if (next.error) return

      this.draft = Object.assign({}, this.draft, { [kind]: next.list })
      this.inputs = Object.assign({}, this.inputs, { [kind]: '' })
    },

    move(kind, index, delta) {
      this.draft = Object.assign({}, this.draft, { [kind]: movePhrase(this.draft[kind], index, delta) })
    },

    remove(kind, index) {
      this.draft = Object.assign({}, this.draft, { [kind]: removePhrase(this.draft[kind], index) })
    },

    save() {
      return this.send(phrasesBodyOf(this.draft), this.$t(this.T.phrasesSaved))
    },

    /** 「기본 문구로 되돌리기」 — 매장 행을 지운다(`null`, V5). 확인 창 하나 */
    async resetDefault() {
      try {
        await this.$confirm(this.$t(this.T.resetConfirm), this.$t(this.T.resetDefault), {
          confirmButtonText: this.$t(this.T.resetDefault),
          cancelButtonText: this.$t('취소'),
          type: 'warning',
        })
      } catch (cancelled) {
        return
      }

      await this.send(PHRASES_RESET_BODY, this.$t(this.T.phrasesSaved))
    },

    /** 저장 응답(GET 과 같은 모양)으로 다시 그리고 화면에 알린다 — 400 의 칸별 문장은 그 목록 아래에 앉힌다 */
    async send(body, doneMessage) {
      this.saving = true
      this.saveError = ''
      this.errors = blankByKind()

      try {
        const response = await this.$axios.put(TEL_INFO_PHRASES_API, body, SAVE_CONFIG)
        const view = response && response.data

        this.draft = phraseDraftOf(view)
        this.$emit('saved', view)
        this.$message({ type: 'success', message: doneMessage })
      } catch (error) {
        const fields = rejectionFieldsOf(httpErrorBody(error))
        const placed = this.kinds.filter((kind) => fields[kind.key])

        this.errors = Object.assign(blankByKind(), ...placed.map((kind) => ({ [kind.key]: fields[kind.key] })))
        if (placed.length === 0) this.saveError = httpErrorMessage(error, this.$t(this.T.phrasesSaveFailed))
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.phrase-dialog__default {
  margin-bottom: 12px;
}
.phrase-dialog__kind + .phrase-dialog__kind {
  margin-top: 16px;
}
.phrase-dialog__title {
  margin: 0 0 4px;
  font-size: 14px;
}
.phrase-dialog__help {
  margin: 4px 0;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}
.phrase-dialog__list {
  margin: 8px 0;
  padding-left: 20px;
}
.phrase-dialog__item {
  align-items: center;
  display: flex;
  gap: 6px;
  min-height: 32px;
}
.phrase-dialog__text {
  flex: 1;
}
.phrase-dialog__add {
  display: flex;
  gap: 8px;
}
.phrase-dialog__error {
  margin: 4px 0 0;
  color: #f56c6c;
  font-size: 12px;
}
</style>
